export const Spinner = ({ full }) => (
  <div className={`flex items-center justify-center ${full ? "min-h-[60vh]" : "py-16"}`}>
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

export const Pill = ({ children, tone = "muted" }) => {
  const tones = {
    muted: "bg-zinc-100 text-zinc-700 border-zinc-300",
    blue: "bg-blue-50 text-primary border-blue-200",
    red: "bg-red-50 text-destructive border-red-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-mono uppercase tracking-wide border ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const StatBox = ({ value, label, testid }) => (
  <div className="border border-zinc-300 bg-white p-6" data-testid={testid}>
    <div className="font-heading font-900 text-3xl lg:text-4xl tracking-tight tabular-nums">{value}</div>
    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
  </div>
);

// Vibrant page themes — blue, green, purple, orange. Backgrounds are deep enough
// for white text (WCAG AA) while staying colorful instead of near-black.
export const VIBRANT_THEMES = [
  { key: "blue", bg: "#1D4ED8", accent: "#BFDBFE" },
  { key: "green", bg: "#047857", accent: "#A7F3D0" },
  { key: "purple", bg: "#6D28D9", accent: "#DDD6FE" },
  { key: "orange", bg: "#C2410C", accent: "#FED7AA" },
];
export const vibrantTheme = (seed = "") => {
  let hash = 0;
  for (const char of String(seed)) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return VIBRANT_THEMES[hash % VIBRANT_THEMES.length];
};

// Clickable category icons baked into the old sub-page banner artwork (kept for reference).

// Slim partner strip — simple TravelerPulse logo, links to the sister site.
export const AviationBanner = ({ testid = "page-header-aviation-panel" }) => (
  <div className="bg-[#eef7fc] border-b border-zinc-200" data-testid={testid}>
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-1.5 flex items-center justify-center sm:justify-end gap-2 min-w-0">
      <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-700 shrink-0">Travel planning by</span>
      <a href="https://travelerpulse.pro" target="_blank" rel="noopener noreferrer"
        data-testid="banner-link-travelerpulse" className="shrink-0 hover:opacity-80 transition-opacity">
        <img src={BRAND.masthead} alt="TravelerPulse.pro" className="h-5 sm:h-6 w-auto" />
      </a>
    </div>
  </div>
);

export const PageHeader = ({ eyebrow, title, subtitle, children, entityCode, plain = false }) => {
  if (plain) {
    return (
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 pb-6" data-testid="page-header">
        {eyebrow && <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">{eyebrow}</div>}
        <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight">{title}</h1>
        {subtitle && <p className="mt-3 text-muted-foreground max-w-2xl">{subtitle}</p>}
        {children}
      </div>
    );
  }

  const theme = vibrantTheme(entityCode || title);
  return (
    <header className="border-b border-zinc-300 bg-white" data-testid="page-header">
      <AviationBanner />
      <div className="text-white" style={{ backgroundColor: theme.bg, borderBottom: `4px solid ${theme.accent}` }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 sm:py-10">
          {eyebrow && <div className="text-xs font-mono uppercase tracking-[0.18em] mb-2" style={{ color: theme.accent }}>{eyebrow}</div>}
          <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight" data-testid="page-header-title">{title}</h1>
          {subtitle && <p className="mt-3 text-white/90 max-w-3xl">{subtitle}</p>}
          {children}
        </div>
      </div>
    </header>
  );
};

export const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString());

export const TabBar = ({ tabs, active, onChange, attached = false }) => (
  <div className={attached ? "border-b border-zinc-300 bg-white overflow-x-auto" : "max-w-7xl mx-auto px-5 lg:px-8 mt-10 border-b border-zinc-300 overflow-x-auto"}
    data-testid={attached ? "entity-tabs-container" : "entity-tab-bar"}>
    <div className={attached ? "max-w-7xl mx-auto px-5 lg:px-8" : ""}>
      <div className="flex gap-1 min-w-max">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => onChange(t.key)} data-testid={`entity-tab-${t.key}`} aria-selected={active === t.key}
            className={`flex items-center gap-2 min-h-11 px-4 sm:px-5 py-3 text-sm font-700 -mb-px border-b-2 whitespace-nowrap transition-colors ${active === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>
    </div>
  </div>
);

import { BRAND, codeGradient } from "../lib/assets";

export const IataBadge = ({ code, name, logo, className = "h-24", size = "text-3xl" }) => {
  const label = code || "—";
  const [g1, g2] = codeGradient(label);
  return (
    <div
      className={`relative w-full ${className} flex flex-col items-center justify-center text-center px-4 overflow-hidden`}
      style={{ backgroundImage: `linear-gradient(135deg, ${g1} 0%, ${g2} 100%)` }}
      data-testid={`iata-badge-${label}`}
    >
      {name && (
        <span className="font-heading font-900 text-white leading-tight text-lg sm:text-xl line-clamp-2 drop-shadow-[0_1px_4px_rgba(0,0,0,0.4)]">
          {name}
        </span>
      )}
      <span className={`font-mono font-700 text-white/95 tracking-wide ${name ? "text-base mt-1" : size + " font-900"} drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)]`}>
        {label}
      </span>
      {logo && (
        <img
          src={logo}
          alt=""
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          onLoad={(e) => { if (e.currentTarget.naturalWidth < 2) e.currentTarget.style.display = "none"; }}
          className="absolute right-2.5 top-2.5 w-9 h-9 object-contain bg-white/95 rounded-md p-1 shadow-sm"
        />
      )}
    </div>
  );
};
